import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "190";
const height = "190";
const viewBox = "0 0 190 190";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    sm: { width: 98, height: 98 },
    md: { width: 101, height: 101 },
    lg: { width: 190, height: 190 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            fillRule="evenodd"
            clipRule="evenodd"
            key="key-0"
            d="M129.008 23H148C149.657 23 151 24.3432 151 26C151 27.6569 149.657 29 148 29H132.286C132.448 30.8005 132.637 33.2447 132.779 36.1379C133.089 42.4189 133.184 50.8751 132.31 59.4812C131.44 68.0398 129.588 76.9952 125.852 84.124C123.504 88.6037 120.345 92.4685 116.166 95C120.345 97.5315 123.504 101.396 125.852 105.876C129.588 113.005 131.44 121.96 132.31 130.519C133.184 139.125 133.089 147.581 132.779 153.862C132.625 157.005 132.416 159.617 132.245 161.451C132.312 161.543 132.378 161.632 132.44 161.717C132.512 161.816 132.581 161.911 132.646 162H148C149.657 162 151 163.343 151 165C151 166.657 149.657 168 148 168H131.065C131.06 168 131.056 168 131.051 168H42C40.3431 168 39 166.657 39 165C39 163.343 40.3431 162 42 162H56.4615C56.575 161.864 56.7005 161.715 56.8375 161.552C56.8441 161.544 56.8508 161.536 56.8575 161.528C56.826 161.221 56.7921 160.879 56.7566 160.503C56.5853 158.695 56.3759 156.116 56.2208 153.013C55.9114 146.821 55.8154 138.482 56.6904 129.995C57.5606 121.555 59.4142 112.719 63.1562 105.682C65.6181 101.053 68.9665 97.0974 73.4336 94.6173C69.1776 92.1069 65.9659 88.2293 63.5858 83.7209C59.8467 76.6383 57.9939 67.7422 57.124 59.243C56.2493 50.6964 56.3453 42.299 56.6546 36.0623C56.7965 33.2031 56.9838 30.7855 57.1458 29H42C40.3431 29 39 27.6569 39 26C39 24.3432 40.3431 23 42 23H60.4259C60.4556 22.9996 60.4855 22.9996 60.5154 23H128.919C128.948 22.9996 128.978 22.9996 129.008 23ZM64.3928 162C66.8993 159.169 70.3186 155.459 74.1178 151.757C78.0163 147.96 82.2495 144.235 86.2624 141.479C90.3954 138.641 93.8218 137.167 96.2173 137.167C98.5716 137.167 101.768 138.606 105.545 141.415C109.219 144.146 113.014 147.846 116.479 151.633C119.916 155.39 122.962 159.157 125.157 162H64.3928ZM126.746 154.353C124.975 152.178 122.933 149.774 120.73 147.367C117.142 143.445 113.056 139.437 108.954 136.387C104.958 133.415 100.487 131 96.2173 131C91.9886 131 87.3188 133.38 83.0336 136.323C78.6282 139.348 74.1212 143.332 70.1073 147.242C67.2357 150.04 64.583 152.833 62.3598 155.267C62.3079 154.466 62.2583 153.613 62.2134 152.714C61.9128 146.699 61.8259 138.689 62.6588 130.611C63.4966 122.485 65.2419 114.539 68.4537 108.499C71.6202 102.545 76.0436 98.7037 82.3423 97.9804C83.237 97.8777 83.9944 97.3912 84.469 96.7042C84.9782 96.2344 85.3281 95.587 85.414 94.8448C85.6044 93.1989 84.4245 91.7103 82.7786 91.5199C76.4886 90.7922 72.0629 86.9264 68.8918 80.9197C65.677 74.8304 63.9309 66.8203 63.0928 58.632C62.2596 50.4911 62.3466 42.4197 62.6473 36.3596C62.7972 33.3359 62.9996 30.8278 63.1639 29.0806L63.1715 29H126.26L126.27 29.099C126.434 30.8595 126.637 33.3866 126.787 36.433C127.087 42.5387 127.174 50.6714 126.341 58.8747C125.502 67.1253 123.755 75.1996 120.538 81.3388C117.372 87.3798 112.961 91.2647 106.71 92.0135C106.458 92.0377 106.214 92.0933 105.983 92.1769C105.131 92.4831 104.472 93.1583 104.174 93.9911C104.06 94.3084 103.999 94.6486 104 95C103.999 95.3514 104.06 95.6916 104.174 96.0089C104.478 96.8622 105.151 97.5227 105.983 97.8231C106.214 97.9067 106.458 97.9623 106.71 97.9865C112.961 98.7353 117.372 102.62 120.538 108.661C123.755 114.8 125.502 122.875 126.341 131.125C127.174 139.329 127.087 147.461 126.787 153.567C126.773 153.833 126.76 154.095 126.746 154.353Z"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Timeout"
});
